import { useEffect, useRef } from 'react'
import { refreshCheckIntoRoom } from 'src/clinic/components/CheckIn/services'
import { isCommonResponseSuccessful } from '../helpers/utilityFunctions'
import {
  broadcastCheckInUpdate,
  broadcastCheckInRemoval,
  updateCheckInWithValidation,
  initCheckInBroadcastChannel,
  cleanupCheckInBroadcastChannel,
  fetchCurrentCheckInStatus,
} from 'src/clinic/components/CheckIn/checkInSyncService'

/**
 * Retry configuration for heartbeat
 */
const HEARTBEAT_RETRY_CONFIG = {
  maxRetries: 2, // Fewer retries for heartbeat (runs frequently)
  initialDelay: 2000, // 2 seconds
  maxDelay: 8000, // 8 seconds
}

/**
 * Sleep utility for retry delays
 */
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

const HeartbeatManager = () => {
  const heartbeatInProgressRef = useRef(false)
  
  const sendHeartbeat = async (attemptNumber = 0, isRetryAfterEmpty = false) => {
    // Prevent concurrent heartbeats
    if (heartbeatInProgressRef.current) {
      return
    }
    
    heartbeatInProgressRef.current = true
    
    try {
      const checkedInDetails = localStorage.getItem('checkedInDetails')
      
      if (!checkedInDetails) {
        // If this is the initial check and we didn't find check-in, wait a bit
        // in case validation is still in progress (validation runs async and may take time with retries)
        if (!isRetryAfterEmpty && attemptNumber === 0) {
          await sleep(3000) // Wait 3 seconds for validation to complete
          
          // Re-check localStorage after waiting
          const recheckedDetails = localStorage.getItem('checkedInDetails')
          if (!recheckedDetails) {
            return
          } else {
            // Continue with the found check-in - parse and process
            const parsedCheckedInDetails = JSON.parse(recheckedDetails)
            const serverCheckIn = await fetchCurrentCheckInStatus(attemptNumber)
            
            if (serverCheckIn) {
              const emrUsageLog = serverCheckIn.emrUsageLog
              const chamberRoom = serverCheckIn.chamberRoom
              
              if (emrUsageLog) {
                updateCheckInWithValidation(emrUsageLog)
                
                if (chamberRoom) {
                  localStorage.setItem('peekedInRoomDetails', JSON.stringify(chamberRoom))
                  broadcastCheckInUpdate(emrUsageLog, chamberRoom)
                } else {
                  broadcastCheckInUpdate(emrUsageLog)
                }
                
              }
            } else {
              // Server doesn't have check-in
              console.warn('[HeartbeatManager] Server has no check-in after re-check, removing from localStorage')
              localStorage.removeItem('checkedInDetails')
              localStorage.removeItem('peekedInRoomDetails')
              localStorage.removeItem('roomDataEventTypes')
              broadcastCheckInRemoval()
            }
            return
          }
        } else {
          return
        }
      }
      
      const parsedCheckedInDetails = JSON.parse(checkedInDetails)
      
      // Use the resilient fetch function with retry logic
      const serverCheckIn = await fetchCurrentCheckInStatus(attemptNumber)
      
      if (serverCheckIn) {
        // Success - update localStorage with fresh data and validation timestamp
        const emrUsageLog = serverCheckIn.emrUsageLog
        const chamberRoom = serverCheckIn.chamberRoom
        
        if (emrUsageLog) {
          // Update with validation timestamp
          updateCheckInWithValidation(emrUsageLog)
          
          // Update chamber room if provided
          if (chamberRoom) {
            localStorage.setItem('peekedInRoomDetails', JSON.stringify(chamberRoom))
            // Broadcast update with chamber room
            broadcastCheckInUpdate(emrUsageLog, chamberRoom)
          } else {
            // Broadcast update without chamber room
            broadcastCheckInUpdate(emrUsageLog)
          }
          
        }
      } else {
        // Server doesn't have check-in or fetch failed after retries
        // Check if it's a network error or server rejection
        const isNetworkError = parsedCheckedInDetails?.validationError === true
        
        if (isNetworkError && attemptNumber < HEARTBEAT_RETRY_CONFIG.maxRetries) {
          // Network error - retry with delay
          const delay = Math.min(
            HEARTBEAT_RETRY_CONFIG.initialDelay * Math.pow(2, attemptNumber),
            HEARTBEAT_RETRY_CONFIG.maxDelay
          )
          await sleep(delay)
          return sendHeartbeat(attemptNumber + 1)
        } else {
          // Server rejected or max retries exceeded - remove check-in
          console.warn('[HeartbeatManager] Server rejected check-in or max retries exceeded, removing from localStorage')
          localStorage.removeItem('checkedInDetails')
          localStorage.removeItem('peekedInRoomDetails')
          localStorage.removeItem('roomDataEventTypes')
          broadcastCheckInRemoval()
        }
      }
    } catch (err) {
      console.error('[HeartbeatManager] Unexpected error during heartbeat:', err)
      
      // On unexpected error, retry if we haven't exceeded max retries
      if (attemptNumber < HEARTBEAT_RETRY_CONFIG.maxRetries) {
        const delay = Math.min(
          HEARTBEAT_RETRY_CONFIG.initialDelay * Math.pow(2, attemptNumber),
          HEARTBEAT_RETRY_CONFIG.maxDelay
        )
        await sleep(delay)
        return sendHeartbeat(attemptNumber + 1)
      } else {
        console.error('[HeartbeatManager] Max retries exceeded, keeping local check-in data')
        // Don't remove check-in on error - network issues shouldn't clear it
      }
    } finally {
      heartbeatInProgressRef.current = false
    }
  }

  useEffect(() => {
    // Initialize BroadcastChannel for multi-tab sync
    initCheckInBroadcastChannel()
    
    // Set interval to send heartbeat every 90 seconds
    const intervalId = setInterval(() => {
      sendHeartbeat(0) // Start with attempt 0
    }, 90 * 1000)
    
    // Send initial heartbeat after a delay (longer to allow validation to complete)
    // Validation starts after 1 second, so we wait 5 seconds to ensure it completes
    const initialTimeout = setTimeout(() => {
      sendHeartbeat(0) // Start with attempt 0
    }, 5000) // Wait 5 seconds after mount to allow validation to complete

    // Clean up interval and timeout on component unmount
    return () => {
      clearInterval(intervalId)
      clearTimeout(initialTimeout)
      cleanupCheckInBroadcastChannel()
    }
  }, [])

  return null // This component does not render anything
}

export default HeartbeatManager
